/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useMemo, type CSSProperties } from 'react';
import AppLoader from '@/components/feedback/AppLoader';
import TrafficLightLegend from '@/components/kpi/TrafficLightLegend';
import { useI18n } from '@/context/I18nContext';
import PlotlyFigure from '@/components/charts/PlotlyFigure';
import { createTimeSeriesPlotlyFigure, getVerticalMarkerValue } from '@/lib/timeSeries/timeSeriesPlotly';
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import type { StoredPlotlyResponse } from '@/types/eeCheck';
import type {
    TimeSeriesBandMarker,
    TimeSeriesLegendNames,
    TimeSeriesTraceColors,
    TimeSeriesPlotType,
    TimeSeriesPlotTrafficLightLegendConfig,
    TimeSeriesTimeBand,
    TimeSeriesVerticalMarker,
    TimeSeriesValueBand,
} from '@/lib/timeSeries/timeSeriesPlotly';

interface TimeSeriesPlotFigureProps {
    chartType?: TimeSeriesPlotType;
    conversion?: number;
    errorMessage?: string;
    fillParent?: boolean;
    figure?: StoredPlotlyResponse | null;
    includeZeroYAxis?: boolean;
    isLoading?: boolean;
    loadingLabel?: string;
    minWidth?: CSSProperties['minWidth'];
    parsedTimeSeries?: ParsedTimeSeriesCsv | null;
    revision?: number;
    bandMarkers?: TimeSeriesBandMarker[];
    legendNames?: TimeSeriesLegendNames;
    traceColors?: TimeSeriesTraceColors;
    right?: string[];
    timeBands?: TimeSeriesTimeBand[];
    title?: string;
    trafficLightLegend?: TimeSeriesPlotTrafficLightLegendConfig;
    verticalMarkers?: TimeSeriesVerticalMarker[];
    valueBands?: TimeSeriesValueBand[];
    xAxisTitle?: string;
    xAxisRange?: [string, string];
    yAxisTitle?: string;
}

function TimeSeriesPlotFigure({
    chartType = 'line',
    conversion = 1,
    errorMessage = '',
    fillParent = false,
    figure,
    includeZeroYAxis = false,
    isLoading = false,
    loadingLabel,
    minWidth,
    parsedTimeSeries,
    revision,
    bandMarkers = [],
    legendNames = {},
    traceColors = {},
    right = [],
    timeBands = [],
    title,
    trafficLightLegend,
    verticalMarkers = [],
    valueBands = [],
    xAxisTitle,
    xAxisRange,
    yAxisTitle,
}: TimeSeriesPlotFigureProps) {
    const { t } = useI18n();
    const plotClassName = `time-series-api-plot${minWidth ? ' time-series-api-plot--min-width' : ''}`;
    const plotStyle = minWidth ? { minWidth } : undefined;
    const displayTimeSeries = useMemo(() => {
        if (!parsedTimeSeries || conversion === 1) {
            return parsedTimeSeries;
        }

        return {
            ...parsedTimeSeries,
            plotYAxisValues: Object.fromEntries(
                Object.entries(parsedTimeSeries.plotYAxisValues).map(([column, values]) => [
                    column,
                    values.map((value) => value * conversion),
                ]),
            ),
        };
    }, [conversion, parsedTimeSeries]);
    const resolvedFigure =
        figure ??
        (displayTimeSeries
            ? createTimeSeriesPlotlyFigure(displayTimeSeries, {
                bandMarkers,
                chartType,
                includeZeroYAxis,
                legendNames,
                traceColors,
                rightAxisSeries: right,
                timeBands,
                title,
                verticalMarkers,
                valueBands,
                xAxisTitle,
                xAxisRange,
                yAxisTitle,
            })
            : null);
    const trafficLightValue = displayTimeSeries && trafficLightLegend && verticalMarkers[0]
        ? getVerticalMarkerValue(displayTimeSeries, verticalMarkers[0])
        : null;

    if (isLoading) {
        return (
            <div
                className={`${plotClassName} time-series-api-plot--loading`}
                style={plotStyle}
            >
                <AppLoader label={loadingLabel ?? t('Loading time series chart...')} />
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className={plotClassName} style={plotStyle}>
                <div className="time-series-api-plot__message">{errorMessage}</div>
            </div>
        );
    }

    if (!resolvedFigure) {
        return (
            <div className={plotClassName} style={plotStyle}>
                <div className="time-series-api-plot__message">
                    {t('No time series chart available.')}
                </div>
            </div>
        );
    }

    return (
        <div className={`${plotClassName}${trafficLightLegend ? ' relative' : ''}`} style={plotStyle}>
            <PlotlyFigure
                figure={resolvedFigure}
                fillParent={fillParent}
                minWidth={minWidth}
                revision={revision}
            />
            {trafficLightLegend ? (
                <TrafficLightLegend
                    fractionDigits={trafficLightLegend.fractionDigits}
                    title={trafficLightLegend.title}
                    trafficLight={trafficLightLegend.trafficLight}
                    unit={trafficLightLegend.unit}
                    value={trafficLightValue}
                />
            ) : null}
        </div>
    );
}

export default TimeSeriesPlotFigure;
